/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Button from '@material-ui/core/Button';
import FormControl from '../FormControl';
import FormFocus from '../../utils/FormFocus';
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      '& .MuiButtonBase-root': {
        borderRadius: function borderRadius(_ref) {
          var edges = _ref.edges;
          switch (edges) {
            case 'rounded':
              return 99;
            case 'sharp':
              return 0;
            default:
              return theme.shape.borderRadius;
          }
        },
        '&:first-child': {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        },
        '&:last-child': {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        }
      }
    },
    checkbox: {
      visibility: 'hidden',
      position: 'absolute'
    },
    selected: {
      borderColor: function borderColor(_ref2) {
        var hasError = _ref2.hasError;
        return hasError ? theme.palette.error.main : theme.palette.primaryAlt.main;
      },
      color: '#fff',
      background: function background(_ref3) {
        var hasError = _ref3.hasError;
        return hasError ? theme.palette.error.main : theme.palette.primaryAlt.main;
      },
      '&:hover': {
        background: function background(_ref4) {
          var hasError = _ref4.hasError;
          return hasError ? theme.palette.error.dark : theme.palette.primaryAlt.dark;
        }
      }
    },
    notselected: {
      borderColor: function borderColor(_ref5) {
        var hasError = _ref5.hasError,
          checked = _ref5.checked,
          indeterminate = _ref5.indeterminate,
          required = _ref5.required;
        return hasError ? theme.palette.error.main : theme.palette.primaryAlt.main;
      },
      color: function color(_ref6) {
        var hasError = _ref6.hasError;
        return hasError ? theme.palette.error.main : theme.palette.primaryAlt.main;
      }
    },
    requiredUnselected: {
      borderColor: function borderColor(_ref7) {
        var checked = _ref7.checked,
          indeterminate = _ref7.indeterminate;
        return (!checked, indeterminate) ? theme.palette.secondary.main : theme.palette.primaryAlt.main;
      },
      color: function color(_ref8) {
        var checked = _ref8.checked,
          indeterminate = _ref8.indeterminate;
        return (!checked, indeterminate) ? theme.palette.secondary.main : theme.palette.primaryAlt.main;
      }
    }
  };
});
function BooleanInput(_ref9) {
  var disabled = _ref9.disabled,
    disableRipple = _ref9.disableRipple,
    fullWidth = _ref9.fullWidth,
    helperText = _ref9.helperText,
    helperTextPlacement = _ref9.helperTextPlacement,
    label = _ref9.label,
    required = _ref9.required,
    onChange = _ref9.onChange,
    edges = _ref9.edges,
    value = _ref9.value,
    showError = _ref9.showError,
    errorMessage = _ref9.errorMessage,
    trueLabel = _ref9.trueLabel,
    falseLabel = _ref9.falseLabel,
    parseBool = _ref9.parseBool,
    name = _ref9.name,
    parseValue = _ref9.parseValue;
  var _useState = useState(true),
    indeterminate = _useState[0],
    setIndeterminate = _useState[1];
  var _useState2 = useState(true),
    checked = _useState2[0],
    setChecked = _useState2[1];
  var hasError = showError && errorMessage;
  var classes = useStyles({
    checked: checked,
    hasError: hasError,
    edges: edges,
    value: value,
    required: required,
    indeterminate: indeterminate
  });
  useEffect(function () {
    if (value !== null && value !== '' && value !== undefined) {
      setIndeterminate(false);
      setChecked(parseBool(value));
    }
  }, [value, parseBool]);
  var handleChange = function handleChange(e, bool) {
    e.preventDefault();
    onChange(parseValue(bool));
  };
  return /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: fullWidth,
    label: label,
    name: name,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormFocus, {
    name: name
  }), /*#__PURE__*/React.createElement(ButtonGroup, {
    className: classes.root
  }, /*#__PURE__*/React.createElement(Button, {
    variant: indeterminate || !checked ? 'outlined' : 'contained',
    onClick: function onClick(e) {
      return handleChange(e, true);
    },
    disableElevation: true,
    disableRipple: disableRipple,
    disableTouchRipple: disableRipple,
    disableFocusRipple: disableRipple,
    type: "button",
    className: indeterminate || !checked ? required ? classes.requiredUnselected : classes.notselected : classes.selected
  }, trueLabel), /*#__PURE__*/React.createElement(Button, {
    variant: indeterminate || checked ? 'outlined' : 'contained',
    onClick: function onClick(e) {
      return handleChange(e, false);
    },
    disableElevation: true,
    disableRipple: disableRipple,
    disableTouchRipple: disableRipple,
    disableFocusRipple: disableRipple,
    type: "button",
    className: indeterminate || checked ? required ? classes.requiredUnselected : classes.notselected : classes.selected
  }, falseLabel))));
}
BooleanInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The input name.
   */
  name: PropTypes.string.isRequired,
  /**
   * Whether or not the 'Yes' option is selected. Pass null if indeterminate.
   */
  value: PropTypes.any,
  /**
   * If `true`, the buttons will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the buttons will not have ripple effect when clicked.
   */
  disableRipple: PropTypes.bool,
  /**
   * If `true`, the buttons will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * Label for the left button
   */
  trueLabel: PropTypes.string,
  /**
   * Label for the right button
   */
  falseLabel: PropTypes.string,
  /**
   * The helper text content.
   */
  helperText: PropTypes.string,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * If `true`, the label is displayed as required and the `input` element` will be required.
   */
  required: PropTypes.bool,
  /**
   * Function to change input value
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * Function for parsing the value as a bool to determine which button is currently selected (true = left)
   */
  parseBool: PropTypes.func,
  /**
   * Function for parsing the value back
   */
  parseValue: PropTypes.func
} : {};
BooleanInput.defaultProps = {
  disabled: false,
  disableRipple: false,
  fullWidth: false,
  helperText: '',
  helperTextPlacement: 'above',
  label: '',
  required: false,
  showError: false,
  errorMessage: '',
  edges: 'normal',
  trueLabel: 'Yes',
  falseLabel: 'No',
  parseBool: function parseBool(value) {
    return !!value;
  },
  parseValue: function parseValue(value) {
    return !!value;
  },
  value: null
};
export default BooleanInput;